import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  IconButton,
  TextField,
  Grid2,
  Autocomplete,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FilterValues } from '../billing.helper';
import { useTranslation } from 'react-i18next';
import { getDrList, DoctorListResponse } from './package.helper';
import { getRequest } from '../../../services/httpService';
import { useAppQuery } from '../../../services/appQuery';



interface FilterModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (values: FilterValues) => void;
}

const FilterModal: React.FC<FilterModalProps> = ({ open, onClose, onApply }) => {
  const [values, setValues] = useState<FilterValues>({});
  const { t } = useTranslation('Packagebilling');

  // Fetch doctor list for treatment doctor autocomplete
  const { data: doctorList, isPending: doctorListPending } = useAppQuery<DoctorListResponse>(
    ['doctorList'],
    () => getRequest<DoctorListResponse>(getDrList()),
    { staleTime: 5 * 60 * 1000 }
  );

  const doctorOptions = (doctorList?.objects || [])
    .map(d => `${d.first_name} ${d.last_name}`.trim())
    .filter(name => name.length > 0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'from_date') {
      // If current to_date is before new from_date, bump it up (restrict rather than error)
      if (values.to_date && values.to_date < value) {
        setValues(prev => ({ ...prev, from_date: value, to_date: value }));
        return;
      }
    }
    if (name === 'to_date') {
      // Prevent selecting a to_date before from_date
      if (values.from_date && value < values.from_date) {
        setValues(prev => ({ ...prev, to_date: prev.from_date }));
        return;
      }
    }
    setValues({ ...values, [name]: value });
  };

  const handleClear = () => {
    setValues({from_date: '', to_date: '', treatment_doctor: ''});
  };

  const handleApply = () => {
    const filled: FilterValues = {};
    if (values.from_date) filled.from_date = values.from_date;
    if (values.to_date) filled.to_date = values.to_date;
    if (values.treatment_doctor) filled.treatment_doctor = values.treatment_doctor;
    onApply(filled);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      data-testid="package-filter-modal"
      PaperProps={{
        sx: { borderRadius: '15px' }
      }}
    >
      <DialogTitle data-testid="package-filter-modal-title" sx={{ fontWeight: 700, fontSize: 22, pb: 1 }}>
        {t('filters')}
        <IconButton
          aria-label={t('close')}
          data-testid="package-filter-modal-close-btn"
          onClick={onClose}
          sx={{ position: 'absolute', right: 16, top: 16 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent data-testid="package-filter-modal-content" sx={{ pt: 0 }}>
        <Box sx={{ mt: 1 }}>
          <Grid2 container spacing={2}>
            <Grid2 size={6}>
              <TextField
                data-testid="package-filter-from-date"
                label={t('from_date')}
                size='small'
                name="from_date"
                type="date"
                value={values.from_date }
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                margin="normal"
              />
            </Grid2>
            <Grid2 size={6}>
              <TextField
                data-testid="package-filter-to-date"
                label={t('to_date')}
                size='small'
                name="to_date"
                type="date"
                value={values.to_date }
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                margin="normal"
                inputProps={{ min: values.from_date || undefined }}
              />
            </Grid2>
            <Grid2 size={6}>
              <Autocomplete
                freeSolo
                size='small'
                options={doctorOptions}
                loading={doctorListPending}
                value={values.treatment_doctor || ''}
                onChange={(_, newValue) => setValues({ ...values, treatment_doctor: newValue || '' })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('treating_doctor')}
                    name="treatment_doctor"
                    placeholder={t('enter_doctor_name')}
                    fullWidth
                    margin="normal"
                    onChange={handleChange}
                  />
                )}
              />
            </Grid2>
          </Grid2>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
        <Button
          data-testid="package-filter-clear-btn"
          onClick={handleClear}
          variant="outlined"
          sx={{
            flex: 1,
            borderRadius: 2,
            fontWeight: 700,
            fontSize: 18,
            height: 48,
            mr: 2,
            borderColor: '#222',
            color: '#222',
            background: '#fff',
            '&:hover': { background: '#f5f5f5', borderColor: '#222' },
          }}
        >
          {t('clear')}
        </Button>
        <Button
          data-testid="package-filter-apply-btn"
          onClick={handleApply}
          variant="contained"
          sx={{
            flex: 1,
            borderRadius: 2,
            fontWeight: 700,
            fontSize: 18,
            height: 48,
            background: '#444',
            color: '#fff',
            boxShadow: 'none',
            '&:hover': { background: '#222' },
          }}
        >
          {t('apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilterModal;
